import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';
import NotFound from '@/pages/NotFound';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) return <NotFound />;

  const related = getRelatedArticles(article, 3);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: '萌之社 MoeLab',
    },
    url: `https://mangast.github.io/article/${article.slug}`,
    isPartOf: { '@id': 'https://mangast.github.io/#website' },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://mangast.github.io/article/${article.slug}`,
    },
    articleSection: article.category,
    keywords: article.tags.join(', '),
  };

  return (
    <SEO
      title={`${article.title} | 萌之社 MoeLab`}
      description={article.excerpt}
      keywords={article.tags.join(',')}
      canonicalUrl={`https://mangast.github.io/article/${article.slug}`}
      ogType="article"
      ogImage={article.image}
    >
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-16">
          <article className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: t('article.breadcrumb.home'), href: '/' },
                  { label: article.category, href: `/category/${article.categorySlug}` },
                  { label: article.title },
                ]}
              />
            </div>

            <div className="mb-4">
              <span className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full whitespace-nowrap">
                {article.category}
              </span>
            </div>

            <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 leading-tight mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-8 pb-8 border-b border-background-200">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                {t('article.published')} {article.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                {article.readTime} {t('article.readTime')}
              </span>
            </div>

            <div className="mb-10 rounded-lg overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-auto object-cover"
              />
            </div>

            <div
              className="prose prose-lg max-w-none text-foreground-800 leading-relaxed article-content"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-6 border-t border-background-200">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-foreground-700">{t('article.tags')}：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            {related.length > 0 && (
              <section className="mt-14">
                <h3 className="text-xl font-bold text-foreground-950 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                  {t('article.related')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {related.map((rel) => (
                    <a
                      key={rel.id}
                      href={`/article/${rel.slug}`}
                      className="group bg-background-50 rounded-lg overflow-hidden border border-background-200 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                    >
                      <div className="h-40 overflow-hidden">
                        <img
                          src={rel.image}
                          alt={rel.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <h4 className="text-sm font-bold text-foreground-950 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2">
                          {rel.title}
                        </h4>
                        <div className="mt-2 text-xs text-foreground-500">
                          {rel.date} · {rel.readTime} 分钟阅读
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              </section>
            )}

            <div className="mt-10 text-center">
              <a
                href={`/category/${article.categorySlug}`}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-background-100 text-foreground-700 rounded-full text-sm font-medium hover:bg-background-200 transition-colors duration-200 cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                {t('article.backToList')}
              </a>
            </div>
          </article>
        </main>
        <Footer />
      </div>
    </SEO>
  );
}